import React from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import styles from './Button.module.css';

type Variant = 'primary' | 'outline' | 'ghost';

type CommonProps = {
  variant?: Variant;
  fullWidth?: boolean;
  className?: string;
  children: React.ReactNode;
};

type AsButton = CommonProps &
  Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'className' | 'children'
  > & { to?: undefined };

type AsLink = CommonProps &
  Omit<LinkProps, 'className' | 'children'> & { to: string };

type Props = AsButton | AsLink;

const MotionLink = motion.create(Link);

const Button: React.FC<Props> = ({
  variant = 'primary',
  fullWidth = false,
  className = '',
  children,
  ...rest
}) => {
  const prefersReducedMotion = useReducedMotion();

  const classes = [
    styles.button,
    styles[variant],
    fullWidth ? styles.fullWidth : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Subtle lift on hover + press feedback on tap.
  const motionProps = prefersReducedMotion
    ? {}
    : {
        whileHover: { y: -2 },
        whileTap: { scale: 0.97 },
        transition: { type: 'spring' as const, stiffness: 400, damping: 22 },
      };

  if ('to' in rest && rest.to !== undefined) {
    return (
      <MotionLink className={classes} {...motionProps} {...(rest as any)}>
        {children}
      </MotionLink>
    );
  }

  return (
    <motion.button className={classes} {...motionProps} {...(rest as any)}>
      {children}
    </motion.button>
  );
};

export default Button;
